import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import clsx from "clsx";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";

export const MentionList = forwardRef(
  (
    {
      command,
      query = "",
    }: {
      command: ({
        id,
        label,
        type,
      }: {
        id: string;
        label: string;
        type: string;
      }) => void;
      query: string;
    },
    ref,
  ) => {
    const { allCurrentEmployees } = useTaggableUsers();

    const items = useMemo(() => {
      const queryLower = query.toLowerCase();
      return allCurrentEmployees.filter((user) => {
        const firstNameLower = user.firstName.toLowerCase();
        const lastNameLower = user.lastName.toLowerCase();
        return (
          firstNameLower.startsWith(queryLower) ||
          lastNameLower.startsWith(queryLower)
        );
      });
    }, [allCurrentEmployees, query]);

    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      command({
        id: item.id.toString(),
        label: item.firstName + " " + item.lastName,
        // TODO: This will need to be updated once we support multiple mention types.
        type: "user-mention",
      });
    };

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length);
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }

        if (event.key === "Enter") {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    return (
      // ! Generally keep in sync with `<PopoverContent>`.
      <div className="w-72 rounded-md border border-slate-200 bg-white p-2 text-sm text-slate-700 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50">
        {items.length ? (
          items.map((item, index) => (
            <button
              className={clsx(
                "w-full rounded-md p-2 text-left hover:bg-slate-100",
                index === selectedIndex && "bg-slate-100",
              )}
              key={index}
              onClick={() => {
                selectItem(index);
              }}
            >
              {item.firstName} {item.lastName}
            </button>
          ))
        ) : (
          <div>No result</div>
        )}
      </div>
    );
  },
);

MentionList.displayName = "MentionList";
